import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ChevronDown, Check, Type, Upload, X, Loader } from 'lucide-react';
import { useBrand } from '@/context/BrandContext';
import { safeLocalStorage } from '@/lib/storage-utils';

interface Font {
  family: string;
  variants: string[];
  subsets: string[];
  category: string;
  kind: string;
  files?: { [key: string]: string };
}

interface CustomFont {
  id: string;
  name: string;
  file: File;
  url: string;
  format: string;
}

interface TypographyDropdownProps {
  value: string;
  onChange: (fontFamily: string) => void;
  isGlobalDarkMode: boolean;
  placeholder?: string;
  className?: string;
  fontType?: 'title' | 'text'; // For inheritance behavior
  showInheritOption?: boolean; // Show "Inherit from Brand Settings" option
  dropdownDirection?: 'down' | 'up'; // Direction for dropdown positioning
}

export const TypographyDropdown: React.FC<TypographyDropdownProps> = ({
  value,
  onChange,
  isGlobalDarkMode,
  placeholder = "Select a font",
  className = "",
  fontType = 'text',
  showInheritOption = false,
  dropdownDirection = 'down'
}) => {
  const { state } = useBrand();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [googleFonts, setGoogleFonts] = useState<Font[]>([]);
  const [customFonts, setCustomFonts] = useState<CustomFont[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedFonts, setLoadedFonts] = useState<Set<string>>(new Set());
  const [fontCache, setFontCache] = useState<Font[]>([]);
  
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Popular fonts to show first
  const popularFonts = [
    'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins',
    'Source Sans Pro', 'Oswald', 'Raleway', 'Nunito', 'Ubuntu', 'Playfair Display',
    'Merriweather', 'PT Sans', 'Noto Sans', 'Helvetica', 'Arial', 'Georgia', 'Times New Roman'
  ];

  // System fonts that don't need loading
  const systemFonts = ['Helvetica', 'Arial', 'Georgia', 'Times New Roman', 'Verdana', 'Tahoma'];

  // Load Google Fonts API with caching
  useEffect(() => {
    const loadGoogleFonts = async () => {
      const apiKey = import.meta.env.VITE_GOOGLE_FONTS_API_KEY;
      
      // Check cache first
      const cachedFonts = safeLocalStorage.getItem('googleFontsCache');
      const cacheTimestamp = safeLocalStorage.getItem('googleFontsCacheTimestamp');
      const cacheExpiry = 24 * 60 * 60 * 1000; // 24 hours

      if (cachedFonts && cacheTimestamp) {
        const isExpired = Date.now() - parseInt(cacheTimestamp) > cacheExpiry;
        if (!isExpired) {
          try {
            const parsed = JSON.parse(cachedFonts);
            setGoogleFonts(parsed);
            setFontCache(parsed);
            return;
          } catch (error) {
            console.error('Failed to parse cached fonts:', error);
          }
        }
      }

      setLoading(true);
      try {
        // Use Google Fonts API if key is available, otherwise use fallback fonts
        if (apiKey) {
          // TODO: Implement Google Fonts API call when API key is provided
          // const response = await fetch(`https://www.googleapis.com/webfonts/v1/webfonts?key=${apiKey}`);
          // const data = await response.json();
          // Process and set the fonts from API response
        }
        
        // Fallback fonts (used when no API key is provided)
        const fallbackFonts: Font[] = [
          { family: 'Inter', variants: ['400', '500', '600', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'webfont' },
          { family: 'Roboto', variants: ['300', '400', '500', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'webfont' },
          { family: 'Open Sans', variants: ['400', '600', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'webfont' },
          { family: 'Lato', variants: ['400', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'webfont' },
          { family: 'Montserrat', variants: ['400', '500', '600', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'webfont' },
          { family: 'Poppins', variants: ['400', '500', '600', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'webfont' },
          { family: 'Source Sans Pro', variants: ['400', '600', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'webfont' },
          { family: 'Oswald', variants: ['400', '500', '600'], subsets: ['latin'], category: 'sans-serif', kind: 'webfont' },
          { family: 'Raleway', variants: ['400', '500', '600', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'webfont' },
          { family: 'Nunito', variants: ['400', '600', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'webfont' },
          { family: 'Ubuntu', variants: ['400', '500', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'webfont' },
          { family: 'Playfair Display', variants: ['400', '700'], subsets: ['latin'], category: 'serif', kind: 'webfont' },
          { family: 'Merriweather', variants: ['400', '700'], subsets: ['latin'], category: 'serif', kind: 'webfont' },
          { family: 'PT Sans', variants: ['400', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'webfont' },
          { family: 'Noto Sans', variants: ['400', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'webfont' },
          { family: 'Fira Sans', variants: ['400', '500', '600', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'webfont' },
          { family: 'Work Sans', variants: ['400', '500', '600', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'webfont' },
          { family: 'DM Sans', variants: ['400', '500', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'webfont' },
          { family: 'Space Grotesk', variants: ['400', '500', '600', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'webfont' },
          { family: 'Manrope', variants: ['400', '500', '600', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'webfont' },
          // System fonts
          { family: 'Helvetica', variants: ['400', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'system' },
          { family: 'Arial', variants: ['400', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'system' },
          { family: 'Georgia', variants: ['400', '700'], subsets: ['latin'], category: 'serif', kind: 'system' },
          { family: 'Times New Roman', variants: ['400', '700'], subsets: ['latin'], category: 'serif', kind: 'system' },
          { family: 'Verdana', variants: ['400', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'system' },
          { family: 'Tahoma', variants: ['400', '700'], subsets: ['latin'], category: 'sans-serif', kind: 'system' }
        ];

        setGoogleFonts(fallbackFonts);
        setFontCache(fallbackFonts);
        
        // Cache the fonts
        safeLocalStorage.setItem('googleFontsCache', JSON.stringify(fallbackFonts));
        safeLocalStorage.setItem('googleFontsCacheTimestamp', Date.now().toString());
      } catch (error) {
        console.error('Failed to load Google Fonts:', error);
      }
      setLoading(false);
    };

    loadGoogleFonts();
  }, []);

  // Load custom fonts from localStorage
  useEffect(() => {
    const savedCustomFonts = safeLocalStorage.getItem('customFonts');
    if (savedCustomFonts) {
      try {
        const parsed = JSON.parse(savedCustomFonts);
        setCustomFonts(parsed);
      } catch (error) {
        console.error('Failed to load custom fonts:', error);
      }
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Lazy load font dynamically
  const loadFont = (fontFamily: string) => {
    if (loadedFonts.has(fontFamily) || systemFonts.includes(fontFamily)) {
      return;
    }

    const link = document.createElement('link');
    link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(/\s+/g, '+')}:wght@400;500;600;700&display=swap`;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    setLoadedFonts(prev => new Set([...prev, fontFamily]));
  };

  // Handle font selection
  const handleFontSelect = (fontFamily: string) => {
    if (fontFamily !== 'inherit') {
      loadFont(fontFamily);
    }
    onChange(fontFamily);
    setIsOpen(false);
    setSearchQuery('');
  };

  // Get brand default font
  const getBrandDefaultFont = () => {
    if (fontType === 'title') {
      return state.brandData?.globalTitleFont || 'Helvetica';
    } else {
      return state.brandData?.globalTextFont || 'Helvetica';
    }
  };

  // Filter fonts based on search with optimized performance
  const filteredCustomFonts = customFonts.filter(font =>
    font.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredGoogleFonts = googleFonts.filter(font =>
    font.family.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Sort Google fonts: popular first, then alphabetical
  const sortedGoogleFonts = filteredGoogleFonts.sort((a, b) => {
    const aIsPopular = popularFonts.includes(a.family);
    const bIsPopular = popularFonts.includes(b.family);
    
    if (aIsPopular && !bIsPopular) return -1;
    if (!aIsPopular && bIsPopular) return 1;
    if (aIsPopular && bIsPopular) {
      return popularFonts.indexOf(a.family) - popularFonts.indexOf(b.family);
    }
    
    return a.family.localeCompare(b.family);
  });

  // Get display value
  const getDisplayValue = () => {
    if (value === 'inherit' && showInheritOption) {
      return `Inherit (${getBrandDefaultFont()})`;
    }
    return value || placeholder;
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Font Selector Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-input text-left transition-colors bg-background text-foreground hover:bg-accent"
      >
        <span 
          className="truncate"
          style={{ 
            fontFamily: value === 'inherit' ? getBrandDefaultFont() : (value || 'inherit')
          }}
        >
          {getDisplayValue()}
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ 
              opacity: 0, 
              y: dropdownDirection === 'down' ? -10 : 10, 
              scale: 0.95 
            }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ 
              opacity: 0, 
              y: dropdownDirection === 'down' ? -10 : 10, 
              scale: 0.95 
            }}
            transition={{ duration: 0.15 }}
            className={`absolute ${
              dropdownDirection === 'down' ? 'top-full mt-2' : 'bottom-full mb-2'
            } left-0 right-0 rounded-lg border border-border shadow-lg z-50 max-h-80 overflow-hidden bg-card`}
            style={{ minWidth: '280px' }}
          >
            <div className={`p-3`}>
              <div className="relative">
                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${
                  'text-muted-foreground'
                }`} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search fonts..."
                  className="w-full pl-10 pr-4 py-2 rounded border border-border text-sm bg-background text-foreground placeholder-muted-foreground"
                />
              </div>
            </div>

            {/* Font List */}
            <div className="overflow-y-auto max-h-60">
              {/* Inherit Option */}
              {showInheritOption && (
                <>
                  <div className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground bg-muted/50">
                    Brand Settings
                  </div>
                  <button
                    onClick={() => handleFontSelect('inherit')}
                    className={`w-full flex items-center justify-between px-3 py-2 text-left transition-colors ${
                      value === 'inherit'
                        ? 'bg-workspace-accent/20 text-workspace-accent'
                        : 'text-foreground hover:bg-accent'
                    }`}
                  >
                    <div className="flex-1">
                      <span 
                        className="block"
                        style={{ fontFamily: getBrandDefaultFont() }}
                      >
                        Inherit from Brand Settings
                      </span>
                      <span className={`text-xs ${
                        'text-muted-foreground'
                      }`}>
                        {getBrandDefaultFont()} • Brand Default
                      </span>
                    </div>
                    {value === 'inherit' && (
                      <Check className="w-4 h-4 text-blue-500" />
                    )}
                  </button>
                  <div className={``} />
                </>
              )}

              {/* Custom Fonts Section */}
              {filteredCustomFonts.length > 0 && (
                <>
                  <div className="px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground bg-muted/50">
                    Custom Fonts
                  </div>
                  {filteredCustomFonts.map((font) => (
                    <button
                      key={font.id}
                      onClick={() => handleFontSelect(font.name)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-left transition-colors ${
                        value === font.name
                          ? 'bg-workspace-accent/20 text-workspace-accent'
                          : 'text-foreground hover:bg-accent'
                      }`}
                    >
                      <div className="flex-1">
                        <span 
                          className="block"
                          style={{ fontFamily: font.name }}
                        >
                          {font.name}
                        </span>
                        <span className={`text-xs ${
                          'text-muted-foreground'
                        }`}>
                          Custom • {font.format.toUpperCase()}
                        </span>
                      </div>
                      {value === font.name && (
                        <Check className="w-4 h-4 text-blue-500" />
                      )}
                    </button>
                  ))}
                  <div className={``} />
                </>
              )}

              {/* Google Fonts Section */}
              <div className={`px-3 py-2 text-xs font-medium uppercase tracking-wide ${
                isGlobalDarkMode ? 'text-gray-400 bg-gray-700/50' : 'text-gray-500 bg-gray-50'
              }`}>
                Google Fonts
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="w-6 h-6 animate-spin text-blue-500" />
                  <span className={`ml-2 text-sm ${
                    isGlobalDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Loading fonts...
                  </span>
                </div>
              ) : (
                <>
                  {sortedGoogleFonts.length === 0 ? (
                    <div className={`px-3 py-4 text-center text-sm ${
                      'text-muted-foreground'
                    }`}>
                      No fonts found matching "{searchQuery}"
                    </div>
                  ) : (
                    sortedGoogleFonts.map((font) => (
                      <button
                        key={font.family}
                        onClick={() => handleFontSelect(font.family)}
                        className={`w-full flex items-center justify-between px-3 py-2 text-left transition-colors ${
                          value === font.family
                            ? 'bg-workspace-accent/20 text-workspace-accent'
                            : 'text-foreground hover:bg-accent'
                        }`}
                        onMouseEnter={() => loadFont(font.family)}
                      >
                        <div className="flex-1">
                          <span 
                            className="block"
                            style={{ fontFamily: font.family }}
                          >
                            {font.family}
                          </span>
                          <span className={`text-xs ${
                            'text-muted-foreground'
                          }`}>
                            {font.category} • {font.variants.length} variants
                            {popularFonts.includes(font.family) && (
                              <span className="ml-1 px-1.5 py-0.5 bg-workspace-accent/20 text-workspace-accent rounded text-xs">
                                Popular
                              </span>
                            )}
                            {font.kind === 'system' && (
                              <span className="ml-1 px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                                System
                              </span>
                            )}
                          </span>
                        </div>
                        {value === font.family && (
                          <Check className="w-4 h-4 text-blue-500" />
                        )}
                      </button>
                    ))
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};